import {
  MapInfoWindowChip,
  MapInfoWindowModel,
  MapInfoWindowRow,
  MapInfoWindowSection
} from '../interfaces/mapInfoWindow';
import { escapeHtml } from './escape-html';

/**
 * Construye el contenido de un InfoWindow de Google Maps con el lenguaje visual único
 * del producto. Es el ÚNICO sitio que arma este HTML.
 *
 * Tres reglas que no se negocian:
 *  1. Una fila sin valor útil no se pinta: el relleno con '-' era la mitad de la sensación
 *     de globo cargado.
 *  2. Todo valor se escapa: el string entra al DOM vía InfoWindow.setContent(), así que el
 *     navegador lo parsea como HTML.
 *  3. Un solo título, el que responde "¿qué es este punto?".
 */
export function buildMapInfoWindow(model: MapInfoWindowModel): string {
  if (!model) return '';

  const parts = [buildHeader(model)];
  const body = [
    buildChips(model.chips),
    buildRows(model.rows),
    buildSections(model.sections),
    buildFooter(model.footer)
  ].join('');

  if (body) parts.push(`<div class="map-iw__body">${body}</div>`);

  return `<div class="map-iw">${parts.join('')}</div>`;
}

function buildHeader(model: MapInfoWindowModel): string {
  // La clase del ícono la controla el desarrollador, no es dato de usuario: no se escapa.
  const icon = model.icon
    ? `<span class="map-iw__icon"><i class="${model.icon}"></i></span>`
    : '';
  const subtitle = hasValue(model.subtitle)
    ? `<div class="map-iw__subtitle">${escapeHtml(model.subtitle)}</div>`
    : '';
  return `<div class="map-iw__header">${icon}`
    + `<div class="map-iw__heading">`
    + `<div class="map-iw__title">${escapeHtml(model.title)}</div>`
    + `${subtitle}</div></div>`;
}

function buildChips(chips: MapInfoWindowChip[]): string {
  const visible = (chips || []).filter(chip => chip && hasValue(chip.text));
  if (!visible.length) return '';
  const items = visible.map(chip =>
    `<span class="map-iw__chip map-iw__chip--${chip.tone || 'neutral'}">${escapeHtml(chip.text)}</span>`
  ).join('');
  return `<div class="map-iw__chips">${items}</div>`;
}

function buildRows(rows: MapInfoWindowRow[]): string {
  const visible = keepUsefulRows(rows);
  if (!visible.length) return '';
  const items = visible.map(row =>
    `<tr><td class="map-iw__label">${escapeHtml(row.label)}</td>`
    + `<td class="map-iw__value">${escapeHtml(row.value)}</td></tr>`
  ).join('');
  return `<table class="map-iw__rows">${items}</table>`;
}

function buildSections(sections: MapInfoWindowSection[]): string {
  return (sections || []).map(section => {
    if (!section) return '';
    const rows = buildRows(section.rows);
    // Una sección sin filas útiles no deja ni encabezado ni separador huérfano.
    if (!rows) return '';
    const label = hasValue(section.label)
      ? `<div class="map-iw__section-label">${escapeHtml(section.label)}</div>`
      : '';
    return `<div class="map-iw__section">${label}${rows}</div>`;
  }).join('');
}

function buildFooter(footer: string): string {
  if (!hasValue(footer)) return '';
  return `<div class="map-iw__footer">${escapeHtml(footer)}</div>`;
}

function keepUsefulRows(rows: MapInfoWindowRow[]): MapInfoWindowRow[] {
  return (rows || []).filter(row => row && hasValue(row.label) && hasValue(row.value));
}

/** '-' cuenta como ausencia: es el relleno que usaban los globos viejos. */
function hasValue(value: string): boolean {
  if (value === null || value === undefined) return false;
  const text = String(value).trim();
  return text !== '' && text !== '-';
}
